import {Box, Button, Dialog, DialogContent, makeStyles, Snackbar, Typography} from '@material-ui/core';
import {Alert, AlertTitle} from '@material-ui/lab';
import MuiAlert from '@material-ui/lab/Alert';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import {observer} from 'mobx-react-lite';
import {SessionProvider} from 'next-auth/react';
import {useCallback, useEffect, useState} from 'react';
import {Redirect, Route, BrowserRouter as Router, Switch} from 'react-router-dom';
import AdvisorIIVUL from '../AdvisorIIVUL/AdvisorIIVUL';
import Agent from '../Agent/Agent';
import {EmailEditorSelection} from '../Agent/ContactForm/EmailEditor/EmailEditorSelection';
import {FormHistory} from '../Agent/ContactForm/FormHistory';
import EditXfers from '../Agent/EditXfers/EditXfers';
import {ToolsPanel} from '../Agent/Tools/Tools';
import Analytics from '../Analytics/Analytics';
import {AnalyticsPanel} from '../Analytics/DailyFormSubmission';
import {DataFeedPanel} from '../Analytics/DataFeeds';
import {WAPanel} from '../Analytics/WAPanel';
import Authorized from '../Auth/Authorized';
import {CMPanel} from '../CaseManager/CM';
import {CustomerPortalDataTable} from '../CaseManager/CustomerPortalDataTable';
import FormModal from '../CaseManager/FormModal';
import RootStore from '../Core/mobx/RootStore';
import Home from '../Home/Home';
import LeaderboardRouter from '../Leaderboard/LeaderboardRouter';
import LeaderboardWidget from '../Leaderboard/LeaderboardWidget';
import Live from '../LiveCall/Live';
import Settings from '../Settings/Settings';
import RouteChangeConfirmDialog, {RouteChangeConfirmDialogProps} from '../UI/RouteChangeConfirmDialog';
import UrlShortener from '../UrlShortener/UrlShortener';
import UserAdmin from '../UserAdmin/UserAdmin';
import {IS_PRODUCTION} from '../constants';
import ClientDataAnalytics from './../Analytics/ClientDataAnalytics';
import AppBar from './AppBar';
import {RealtimeProvider} from './RealtimeProvider';
import RouterInterceptor from './RouterInterceptor';

const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const ONE_HOUR_MS = 60 * 60 * 1000;
const AI_AGREEMENT_KEY = 'ai_agreement_accepted_on_timestamp';

const useStyles = makeStyles((theme) => ({
    dialog: {
        '& .MuiPaper-root': {},
        '& #agreementDialogContainer': {},
        '& .MuiDialog-paper': {
            margin: 0,
            width: '640px',
            height: 'auto',
            padding: '24px',
            borderRadius: '25px',
            background: '#fff',
        },
        '& .MuiDialogContent-root': {},
        '& .MuiButton-root': {
            minWidth: '240px',
            minHeight: '64px',
            border: '2px solid #5ae288',
            fontFamily: 'FredokaRegular',
            fontSize: '18px',
            letterSpacing: '0.05em',
            color: '#174943',
            padding: '12px 40px',
            backgroundColor: '#5ae288',
            borderRadius: 102,
            boxSizing: 'border-box',
            transition: 'background-color 0.2s',
            marginLeft: 'auto',
            marginRight: 'auto',
            display: 'block',
            '&:hover': {
                color: '#174943',
                borderColor: 'transparent',
                backgroundColor: '#9cedb7',
            },
            '&.Mui-focusVisible': {
                color: '#174943',
                borderColor: 'transparent',
                backgroundColor: '#9cedb7',
            },
            '&.Mui-disabled': {
                color: '#00000042',
                borderColor: 'transparent',
                backgroundColor: '#0000001f',
            },
        },
    },
    dialogTitle: {
        fontFamily: 'LoraRegular',
        fontWeight: 600,
        fontSize: '32px',
        lineHeight: '34px',
        color: '#456d69',
    },
    dialogContent: {
        fontFamily: 'FredokaRegular',
        fontWeight: 400,
        fontSize: '18px',
        lineHeight: '160%',
        color: '#456d69',
        marginTop: '24px',
        marginBottom: '24px',
    },
}));

const App = observer(() => {
    const uiStore = RootStore.uiStore;
    const classes = useStyles();
    const hideSnack = useCallback(
        AwesomeDebouncePromise(async () => {
            uiStore.closeSnackbar();
        }, 3000),
        [],
    );
    const drawerClosedWidth = 61;
    const drawerOpenWidth = 320;
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [showAgreementDialog, setShowAgreementDialog] = useState(false);
    const [confirmDialogConfig, setConfirmDialogConfig] = useState<RouteChangeConfirmDialogProps>({
        message: 'Are you sure?',
    });
    const getUserConfirmation = (message: string, callback: (ok: boolean) => void) => {
        setConfirmDialogConfig({
            message,
            callback,
        });
    };
    const checkAIAgreementStatus = () => {
        const lastShown = localStorage.getItem(AI_AGREEMENT_KEY);
        const timestamp = Number(lastShown);
        if (!lastShown || Number.isNaN(timestamp) || Date.now() - timestamp > ONE_WEEK_MS) setShowAgreementDialog(true);
    };
    const handleAIAgreementClick = () => {
        localStorage.setItem(AI_AGREEMENT_KEY, Date.now().toString());
        setShowAgreementDialog(false);
    };

    useEffect(() => {
        RootStore.fetchAgents();
        const initialTimeout = setTimeout(checkAIAgreementStatus, 1000);
        const interval = setInterval(checkAIAgreementStatus, ONE_HOUR_MS);
        return () => {
            clearTimeout(initialTimeout);
            clearInterval(interval);
        };
    }, []);

    return (
        <SessionProvider refetchInterval={100}>
            <Authorized>
                {!IS_PRODUCTION && (
                    <div
                        style={{
                            marginLeft: `${isDrawerOpen ? drawerOpenWidth : drawerClosedWidth}px`,
                        }}
                    >
                        <Alert severity="warning">
                            <AlertTitle>Warning</AlertTitle>
                            <strong>
                                {typeof window === 'undefined' ||
                                window.location.hostname === 'ap-preprod.beamplify.com' ? (
                                    <>
                                        This is a pre-production endpoint with the newest features. Only the designated
                                        testers should be using this endpoint. Please report any issues in{' '}
                                        <a href="https://beamplify.slack.com/archives/C05JG08L0CB">
                                            #ap-preprod-testing
                                        </a>{' '}
                                        channel.
                                    </>
                                ) : (
                                    <>
                                        This is a staging environment. Please use{' '}
                                        <a href="https://platform.beamplify.com">Production endpoint</a>
                                    </>
                                )}
                            </strong>
                        </Alert>
                    </div>
                )}
                <Dialog onClose={() => {}} fullScreen className={classes.dialog} open={showAgreementDialog}>
                    <DialogContent>
                        <Box id="agreementDialogContainer">
                            <Typography variant="h6" className={classes.dialogTitle}>
                                Artificial Intelligence Disclaimer
                            </Typography>
                            <Typography variant="body1" className={classes.dialogContent}>
                                Amplify provides AI tools and information for internal purposes only and has not been
                                reviewed for accuracy. Please use your judgment and verify the information before
                                relying on it to advise a client. Do not enter or share any sensitive or personally
                                identifiable information, including names, emails, phone numbers, Social Security
                                numbers, driver's license numbers, banking details, health or prescription details, or
                                any other sensitive information or data that may be used to identify the client. Failure
                                to safeguard our clients' personal or sensitive data may result in disciplinary action,
                                up to and including termination of employment. By clicking "I Agree" below you agree to
                                the above and will comply.
                            </Typography>
                            <Button
                                disableElevation
                                variant="contained"
                                color="primary"
                                onClick={handleAIAgreementClick}
                            >
                                I Agree
                            </Button>
                        </Box>
                    </DialogContent>
                </Dialog>
                <Router getUserConfirmation={getUserConfirmation}>
                    <RouterInterceptor>
                        <RealtimeProvider>
                            <EmailEditorSelection />
                            <LeaderboardWidget />
                            <RouteChangeConfirmDialog
                                message={confirmDialogConfig.message}
                                callback={confirmDialogConfig.callback}
                            />
                            <Snackbar
                                open={!!uiStore.snackbar}
                                autoHideDuration={uiStore.snackbar?.duration}
                                onClose={hideSnack}
                            >
                                {uiStore.snackbar ? (
                                    <div
                                        onClick={() => {
                                            uiStore.snackbar?.action?.onClick?.();
                                            hideSnack();
                                        }}
                                        style={{
                                            cursor: uiStore.snackbar?.action?.onClick ? 'pointer' : 'default',
                                        }}
                                    >
                                        <MuiAlert elevation={6} variant="filled" severity={uiStore.snackbar?.type}>
                                            {uiStore.snackbar?.message}
                                        </MuiAlert>
                                    </div>
                                ) : undefined}
                            </Snackbar>
                            <AppBar drawerState={isDrawerOpen} onDrawerStateChanged={setIsDrawerOpen} />
                            <div style={{marginLeft: `${isDrawerOpen ? drawerOpenWidth : drawerClosedWidth}px`}}>
                                <Switch>
                                    <Route exact path="/agent">
                                        <Agent />
                                    </Route>

                                    <Redirect exact from="/agent/newform" to="/agent/newform/0" />

                                    <Route
                                        exact
                                        path="/agent/newform/:tabIndex"
                                        render={(props) => (
                                            <FormModal
                                                match={props.match}
                                                history={props.history}
                                                location={props.location}
                                                store={RootStore.agentPortalStore}
                                            />
                                        )}
                                    />

                                    <Redirect
                                        exact
                                        from="/agent/form/:email/:formId"
                                        to="/agent/form/:email/:formId/0"
                                    />

                                    <Route
                                        exact
                                        path="/agent/form/:email/:formId/history"
                                        render={(props) => (
                                            <FormHistory
                                                store={RootStore.agentPortalStore}
                                                history={props.history}
                                                match={props.match}
                                                location={props.location}
                                            />
                                        )}
                                    />

                                    <Route
                                        exact
                                        path="/agent/form/:email/:formId/:tabIndex"
                                        render={(props) => (
                                            <FormModal
                                                match={props.match}
                                                history={props.history}
                                                location={props.location}
                                                store={RootStore.agentPortalStore}
                                            />
                                        )}
                                    />
                                    <Route
                                        exact
                                        path="/agent/form/:email/:formId/history/:historyFormId/:tabIndex"
                                        render={(props) => (
                                            <FormModal
                                                match={props.match}
                                                history={props.history}
                                                location={props.location}
                                                store={RootStore.agentPortalStore}
                                            />
                                        )}
                                    />
                                    <Redirect
                                        exact
                                        from="/case-manager/form/:email/:formId"
                                        to="/case-manager/form/:email/:formId/0"
                                    />
                                    <Route
                                        exact
                                        path="/case-manager/form/:email/:formId/:tabIndex"
                                        render={(props) => (
                                            <FormModal
                                                match={props.match}
                                                history={props.history}
                                                location={props.location}
                                                store={RootStore.agentPortalStore}
                                            />
                                        )}
                                    />
                                    <Route exact path="/case-manager/customer-portal-data/">
                                        <CustomerPortalDataTable />
                                    </Route>
                                    <Route exact path="/case-manager">
                                        <CMPanel />
                                    </Route>
                                    <Route exact path="/tools">
                                        <ToolsPanel parameterStore={null} />
                                    </Route>
                                    <Route exact path="/analytics">
                                        <Analytics />
                                    </Route>
                                    <Route exact path="/settings">
                                        <Settings />
                                    </Route>
                                    <Route exact path="/widget-analytics">
                                        <WAPanel />
                                    </Route>
                                    <Route exact path="/client-data-analytics">
                                        <ClientDataAnalytics />
                                    </Route>
                                    <Route exact path="/advisor-ii-vul">
                                        <AdvisorIIVUL />
                                    </Route>
                                    <Route exact path="/daily-form-submission">
                                        <AnalyticsPanel />
                                    </Route>
                                    <Route exact path="/data-feeds">
                                        <DataFeedPanel />
                                    </Route>
                                    <Route exact path="/edit/xfers">
                                        <EditXfers />
                                    </Route>
                                    <Route exact path="/">
                                        <Home />
                                    </Route>
                                    <Route exact path="/user-admin">
                                        <UserAdmin />
                                    </Route>
                                    <Route exact path="/url-shortener">
                                        <UrlShortener />
                                    </Route>
                                    <Route exact path="/live-call">
                                        <Live />
                                    </Route>
                                    <Route exact path="/leaderboard">
                                        <LeaderboardRouter />
                                    </Route>
                                </Switch>
                            </div>
                        </RealtimeProvider>
                    </RouterInterceptor>
                </Router>
            </Authorized>
        </SessionProvider>
    );
});

export default App;
